import type { Schemas } from "@norn/sdk"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { ChevronLeft, ChevronRight, Database, Lock, RotateCw } from "lucide-react"
import { useMemo, useState } from "react"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { OpenInGraph } from "@/components/graph/open-in-graph"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { ActionBar } from "@/components/ui/action-bar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { Value } from "@/components/value"
import { type DefinitionSet, entityDef } from "@/lib/defs"
import { type MessageKey, useT } from "@/lib/i18n"
import { COMMAND_TASK_TYPE, PAGE_SIZE, useDefinitions, useObjects } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { asArray, asString, isRecord } from "@/lib/utils"

interface ObjectTypeEntry {
  readonly name: string
  readonly artifactId: string | null
  readonly layer: "tenant" | "registry" | "system"
  readonly properties: readonly string[]
  readonly description: string | undefined
  readonly descriptionKey?: MessageKey
}

/** Concrete object types the cell can hold: installed object types plus the system task type. */
export function objectTypes(set: DefinitionSet): ObjectTypeEntry[] {
  const out: ObjectTypeEntry[] = []
  for (const a of set.artifacts) {
    if (a.kind !== "entity") continue
    const def = entityDef(a)
    if (def?.form !== "objectType") continue
    out.push({
      name: def.name,
      artifactId: a.artifactId,
      layer: a.layer,
      description: def.description,
      properties: asArray(def.body.properties)
        .filter(isRecord)
        .map((p) => asString(p.id) ?? "?"),
    })
  }
  out.sort((a, b) => (a.layer === b.layer ? a.name.localeCompare(b.name) : a.layer === "tenant" ? -1 : 1))
  out.push({
    name: COMMAND_TASK_TYPE,
    artifactId: null,
    layer: "system",
    description: undefined,
    descriptionKey: "objects.commandFailuresDescription",
    properties: ["status", "actionId", "objectType", "objectKey", "resolution"],
  })
  return out
}

export function ObjectTypesPage() {
  const t = useT()
  const scope = useScope()
  const defs = useDefinitions(scope)
  const types = useMemo(() => (defs.data ? objectTypes(defs.data) : []), [defs.data])
  return (
    <>
      <PageHeader crumbs={[t("nav.objects")]} />
      <div className="flex-1 overflow-y-auto">
        <Screen width="wide" className="px-4 py-6 md:px-6">
          <ContentHeader description={t("objects.description")} />
          {defs.isPending ? (
            <TableSkeleton rows={6} columns={2} />
          ) : defs.isError ? (
            <ErrorState error={defs.error} what={t("objects.objectTypes")} onRetry={() => void defs.refetch()} inline />
          ) : (
            <Section>
              <HairlineTable
                rows={types}
                getRowKey={(entry) => entry.name}
                minWidthClassName="min-w-[480px]"
                columns={[
                  {
                    key: "type",
                    header: t("objects.objectTypes"),
                    cell: (entry) => (
                      <Link
                        to="/$orgId/$environmentId/objects/$objectType"
                        params={{ ...scope, objectType: entry.name }}
                        className="font-medium hover:text-primary"
                      >
                        {entry.name}
                      </Link>
                    ),
                  },
                  {
                    key: "description",
                    header: t("definitionDetail.summary"),
                    cell: (entry) => (
                      <span className="text-muted-foreground">
                        {entry.descriptionKey ? t(entry.descriptionKey) : (entry.description ?? "")}
                      </span>
                    ),
                  },
                  {
                    key: "layer",
                    header: t("definitions.layer"),
                    cell: (entry) =>
                      entry.layer === "registry" ? (
                        <Lock className="size-3.5 text-muted-foreground" aria-label={t("objects.registryBaseType")} />
                      ) : entry.layer === "system" ? (
                        <Badge tone="outline">{t("objects.system")}</Badge>
                      ) : null,
                  },
                ]}
              />
            </Section>
          )}
        </Screen>
      </div>
    </>
  )
}

export function ObjectListPage() {
  const { objectType } = useParams({ from: "/$orgId/$environmentId/objects/$objectType" })
  // Keyed by type so paging state never carries across types.
  return <ObjectList key={objectType} objectType={objectType} />
}

function ObjectList({ objectType }: { objectType: string }) {
  const t = useT()
  const scope = useScope()
  const navigate = useNavigate()
  const defs = useDefinitions(scope)
  // Cursors of the pages before the current one; the opaque token only goes forward.
  const [trail, setTrail] = useState<(string | null)[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const page = useObjects(scope, objectType, cursor)
  const columns = useMemo(() => {
    // Declared order first, then effective properties (inherited, interface, extension) the objects carry.
    const declared = defs.data ? (objectTypes(defs.data).find((t) => t.name === objectType)?.properties ?? []) : []
    const carried = (page.data?.items ?? []).flatMap((o) => Object.keys(o.values as Record<string, unknown>))
    return [...new Set([...declared, ...carried])].slice(0, 5)
  }, [defs.data, objectType, page.data])

  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="o" to="/$orgId/$environmentId/objects" params={scope}>
            {t("nav.objects")}
          </Link>,
          objectType,
        ]}
        actions={
          <Button
            size="icon"
            variant="ghost"
            onClick={() => void page.refetch()}
            disabled={page.isFetching}
            aria-label={t("common.refresh")}
          >
            <RotateCw className={page.isFetching ? "animate-spin" : undefined} />
          </Button>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="px-4 py-6 md:px-6">
          <div>
            {page.isPending ? (
              <TableSkeleton columns={Math.max(columns.length, 3)} />
            ) : page.isError ? (
              <ErrorState
                error={page.error}
                what={t("objects.typeObjects", { name: objectType })}
                onRetry={() => void page.refetch()}
              />
            ) : page.data.items.length === 0 ? (
              <EmptyState icon={<Database />} title={t("objects.noReadableObjects", { name: objectType })}>
                <p>{t("objects.emptyDescription", { orgId: scope.orgId, environmentId: scope.environmentId })}</p>
              </EmptyState>
            ) : (
              <Section>
                <HairlineTable
                  rows={page.data.items}
                  getRowKey={(row) => row.key}
                  onRowClick={(row) =>
                    void navigate({
                      to: "/$orgId/$environmentId/objects/$objectType/$key",
                      params: { ...scope, objectType, key: row.key },
                    })
                  }
                  columns={[
                    {
                      key: "key",
                      header: t("definitionSummary.key"),
                      cell: (row) => (
                        <span className="flex items-center gap-1.5">
                          <Link
                            to="/$orgId/$environmentId/objects/$objectType/$key"
                            params={{ ...scope, objectType, key: row.key }}
                            onClick={(event) => event.stopPropagation()}
                            className="font-mono text-xs hover:text-primary"
                          >
                            {row.key}
                          </Link>
                          {row.observedDeleted ? <Badge tone="danger">{t("objects.deletedInSource")}</Badge> : null}
                        </span>
                      ),
                    },
                    ...columns.map((property) => ({
                      key: `property:${property}`,
                      header: <code className="font-mono text-2xs">{property}</code>,
                      cellClassName: "max-w-64",
                      cell: (row: Schemas["ObjectResponse"]) => (
                        <Value value={(row.values as Record<string, unknown>)[property]} />
                      ),
                    })),
                    {
                      key: "version",
                      header: t("objects.version"),
                      align: "end",
                      cell: (row) => <span className="font-mono text-xs text-muted-foreground">{row.version}</span>,
                    },
                    {
                      key: "graph",
                      header: <span className="sr-only">{t("graph.open")}</span>,
                      align: "end",
                      cell: (row) => <OpenInGraph object={{ objectType: row.objectType, key: row.key }} />,
                    },
                  ]}
                />
              </Section>
            )}
          </div>
          <ActionBar
            className="max-md:bottom-0"
            status={
              <span className="text-xs text-muted-foreground tabular">
                {t("objects.pageSummary", { page: trail.length + 1, limit: PAGE_SIZE })}
              </span>
            }
          >
            <Button
              size="sm"
              disabled={trail.length === 0 || page.isFetching}
              onClick={() => {
                setCursor(trail[trail.length - 1] ?? null)
                setTrail((t) => t.slice(0, -1))
              }}
            >
              <ChevronLeft className="rtl-mirror" /> {t("common.previous")}
            </Button>
            <Button
              size="sm"
              disabled={!page.data?.nextCursor || page.isFetching}
              onClick={() => {
                const next = page.data?.nextCursor
                if (!next) return
                setTrail((t) => [...t, cursor])
                setCursor(next)
              }}
            >
              {t("common.next")} <ChevronRight className="rtl-mirror" />
            </Button>
          </ActionBar>
        </Screen>
      </div>
    </>
  )
}
